import { Box, CircularProgress, Typography } from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import ChangelogEntryComponent from '../components/Changelog/ChangelogEntryComponent.tsx';
import { useChangelogApi } from '../services/api/useChangelog.api';
import { ApiResponse } from '../types/ApiResponse.type';
import { Changelog } from '../types/Changelog/Changelog.type';
import { ChangelogStatus } from '../types/enums/ChangelogStatus.enum.ts';
import { useUserApi } from '../services/api/useUser.api.ts';

const ChangelogPage = () => {
  const queryClient = useQueryClient();
  const { getUserInfo, updateUser } = useUserApi();
  const { getChangelogs } = useChangelogApi();
  const { ref, inView } = useInView();

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['changelogs'],
    queryFn: ({ pageParam = 1 }) =>
      getChangelogs({ page: pageParam, per_page: 10, sort: 'release_date:desc', status_id: ChangelogStatus.PUBLISHED }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Changelog[]>) => {
      const pagination = lastPage.meta.pagination;
      if ((pagination?.page ?? 0) < (pagination?.pages ?? 0)) {
        return (pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const { data: user } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });

  const { mutate: resetChangelog } = useMutation({
    mutationKey: ['resetChangelog'],
    mutationFn: () => updateUser(user!.id, { unread_changelogs_count: 0 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userInfo'], exact: false });
    },
  });

  useEffect(() => {
    if (user?.id && user.unread_changelogs_count > 0) {
      resetChangelog();
    }
  }, [user?.id, user?.unread_changelogs_count, resetChangelog]);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const changelogEntries = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      <Box
        sx={{
          background: 'linear-gradient(94deg, #1b437f -21.51%, #021635 101.1%)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
        }}
      >
        <Typography variant='h4' sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
          Changelog
        </Typography>
      </Box>

      <Box sx={{ maxWidth: '900px', mx: 'auto', paddingY: 8 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && !isLoading && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '12px', paddingY: 8 }}>
            Something went wrong while loading the changelog. Please try again later.
          </Typography>
        )}

        {!isLoading && !isError && changelogEntries.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '12px', paddingY: 8 }}>
            No changelog entries yet.
          </Typography>
        )}

        {changelogEntries.map((entry) => (
          <ChangelogEntryComponent key={entry.id} changelogEntry={entry} />
        ))}

        {hasNextPage && (
          <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', paddingY: 4 }}>
            {isFetchingNextPage && <CircularProgress size={24} />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChangelogPage;
